/**
 * authSkipReload.ts — "Skip for now" actually leaves the registration flow.
 *
 * 🔴 THE DEFECT, found on staging 2026-09-01 by registering an account.
 *
 * app.js:6709 is the whole handler:
 *
 *     $("#stripe-skip")?.addEventListener("click", () => location.assign("/#/"));
 *
 * The page is ALREADY on a hash route when that runs, so `location.assign` with
 * a hash-only URL is a same-document navigation and NOTHING RELOADS. The modal
 * stays exactly where it is, the freshly created account is never picked up, and
 * the person is stranded on the "YOUR ACCOUNT IS READY" card with a button that
 * appears to do nothing.
 *
 * 🪤 IT IS THE SAME DEFECT AS THE SIGN-IN HANG, in a second place. app.js:6688
 * does `location.assign("/#/profile")` after a successful sign-in and its own
 * comment calls that "a HARD reload". Neither one reloads. Two call sites, one
 * misreading of what `location.assign` does to a hash-only URL. The sign-in half
 * is fixed in authSeam.tsx, which we own; this half has no seam call to hang a
 * fix on, so it needs a listener.
 *
 * ══ WHY A CAPTURE LISTENER AND NOT AN EDIT ══
 *
 * app.js is Blake's file and is byte-sacred (vacation-handoff card §2): our layer
 * works in src/web/* and public/web/overrides.css, never in his bundle. So we run
 * ALONGSIDE his handler rather than replacing it.
 *
 * Capture phase on `document`, the same posture writeGuard, listingEdit and
 * profileEdit all use. We do NOT call stopPropagation: his handler must still
 * run, because it is the thing that sets the hash to "/#/". We only supply the
 * reload it assumes it is getting.
 *
 * 🔑 ORDER IS THE WHOLE FIX. His listener sets the hash synchronously during
 * bubble; ours schedules a macrotask during capture. A macrotask cannot run until
 * the current task — including his handler — has finished, so the sequence is
 * always: hash set, then reload. Scheduling a microtask instead would race, and
 * losing that race means reloading on the old hash and landing on the wrong page.
 *
 * ══ WHY NOT ALSO HANDLE #stripe-start ══
 *
 * That one is correct as written. On success the document is already navigating
 * to stripe.com, a real cross-document navigation, so no help is needed. Tyler
 * confirmed on staging that the Stripe button itself worked; only the return
 * journey was wrong, and that is the missing post-register openTutorial wiring
 * (card 1217968527039747), a separate piece of work.
 */

/** His skip button, and only his skip button. */
export const SKIP_SELECTOR = "#stripe-skip";

export function installAuthSkipReload(
  win: Window,
  {
    /** Injected so tests can assert the call without navigating jsdom. */
    reload = () => win.location.reload(),
    /** Macrotask by default. See the ORDER note above; a microtask would race. */
    schedule = (fn: () => void) => win.setTimeout(fn, 0),
  }: { reload?: () => void; schedule?: (fn: () => void) => void } = {},
): () => void {
  const onClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.(SKIP_SELECTOR)) return;
    /* No stopPropagation, deliberately: his handler sets the hash and must run.
       We only add the reload it already believes it is doing. */
    schedule(reload);
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
